/**
 * ADR-118 — WebSocket close-on-logout hook.
 *
 * The lib doesn't own the WebSocket connection — that's app-level
 * code (`EverythingConsumer` client in AIM / SMS / etc.). But on
 * logout, the WS MUST close so the server-side consumer can release
 * its session and stop pushing updates for an unauthenticated user.
 *
 * The lib offers a register/unregister API. Apps wire their WS
 * close callback once at provider-mount; the provider invokes it
 * synchronously inside `logout()` before clearing storage / cookies.
 *
 * Close code is **4001** per ADR-049 (token-refresh code, reused
 * here since the auth state has changed). The server-side close
 * handler differentiates auth-state-change from a graceful
 * shutdown (4010) or a pong timeout (4000).
 */

/**
 * Close code emitted by the lib on logout. ADR-049 reserves the
 * 4000-block for app-defined codes; 4001 is "token refresh" which
 * is the closest existing semantic — the client's auth changed
 * underneath the connection.
 */
export const WS_CLOSE_CODE_LOGOUT = 4001;

/**
 * The callback shape apps register. Receives the close code so the
 * app's WS client can match the existing close-code handling (e.g.
 * the suppress-reconnect logic on 4001 in iOS / Android clients).
 */
export type WebSocketCloseCallback = (code: number) => void;

/**
 * In-module registry. One callback at a time per page — apps
 * shouldn't register multiple WS clients; if they do, the latest
 * wins.
 */
let registered: WebSocketCloseCallback | null = null;

/**
 * Register the WebSocket close callback. Returns an unregister
 * function the caller MUST invoke on component unmount.
 *
 * Example:
 *
 *   useEffect(() => {
 *     return registerWebSocketCloseHook((code) => ws.close(code));
 *   }, [ws]);
 */
export function registerWebSocketCloseHook(
  callback: WebSocketCloseCallback,
): () => void {
  registered = callback;
  return () => {
    if (registered === callback) {
      registered = null;
    }
  };
}

/**
 * Invoke the registered WS close callback with the logout close
 * code. Called by `libs/auth-identity`'s `logout()` action inside
 * the provider; apps don't call this directly.
 *
 * No-op if no callback is registered — apps without a WS connection
 * (some onboarding flows, the ONE app today) just skip the close.
 */
export function fireWebSocketClose(
  code: number = WS_CLOSE_CODE_LOGOUT,
): void {
  if (!registered) return;
  try {
    registered(code);
  } catch (err) {
    // Defensive: the WS close shouldn't be allowed to throw out of
    // the logout path. Log and swallow.
    console.warn('[auth-core] WS close hook threw:', err);
  }
}

/**
 * Test helper — reset the registry between unit tests. NOT for
 * production use.
 */
export function _resetWebSocketHookForTests(): void {
  registered = null;
}
